// ----------------------------------------------------------------------------
// 🔹 TIER UTILITY FUNCTIONS
// Normalize and format subscription tier names across frontend and backend
// ----------------------------------------------------------------------------

/**
 * Normalize tier to lowercase (backend format)
 */
export type SubscriptionTier = 'free' | 'basic' | 'founders' | 'pro' | 'enterprise';

export const normalizeTier = (tier: string): SubscriptionTier => {
  const normalized = tier.toLowerCase();
  if (['free', 'basic', 'founders', 'pro', 'enterprise'].includes(normalized)) {
    return normalized as SubscriptionTier;
  }
  return 'free'; // Default fallback
};

/**
 * Format tier for display (Title Case)
 */
export const displayTierName = (tier: string): string => {
  return tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase();
};

/**
 * Check if a tier is valid
 */
export const isValidTier = (tier: string): boolean => {
  const normalized = tier.toLowerCase();
  return ['free', 'basic', 'founders', 'pro', 'enterprise'].includes(normalized);
};

/**
 * Compare two tiers for equality (case-insensitive)
 */
export const tiersEqual = (tier1: string, tier2: string): boolean => {
  return tier1.toLowerCase() === tier2.toLowerCase();
};

/**
 * Get tier index for comparison (higher index = higher tier)
 */
export const getTierIndex = (tier: string): number => {
  const tierOrder = ['free', 'basic', 'founders', 'pro', 'enterprise'];
  return tierOrder.indexOf(tier.toLowerCase());
};

/**
 * Check if tier1 is an upgrade from tier2
 */
export const isUpgrade = (fromTier: string, toTier: string): boolean => {
  return getTierIndex(toTier) > getTierIndex(fromTier);
};

/**
 * Check if tier1 is a downgrade from tier2
 */
export const isDowngrade = (fromTier: string, toTier: string): boolean => {
  return getTierIndex(toTier) < getTierIndex(fromTier);
};

/**
 * Get tier limits (members and plans)
 */
export const getTierLimits = (tier: string): { maxMembers: number; maxPlans: number; maxGroups: number; maxSongs: number } => {
  const tierLimits = {
    free: { maxMembers: 5, maxPlans: 3, maxGroups: 1, maxSongs: 10 },
    basic: { maxMembers: 50, maxPlans: 999, maxGroups: 20, maxSongs: 200 },
    founders: { maxMembers: 200, maxPlans: 999, maxGroups: 100, maxSongs: 1000 },
    pro: { maxMembers: 200, maxPlans: 999, maxGroups: 100, maxSongs: 1000 },
    enterprise: { maxMembers: 999999, maxPlans: 999, maxGroups: 999999, maxSongs: 999999 },
  };
  
  const normalized = normalizeTier(tier);
  return tierLimits[normalized];
};

/**
 * Get Song Studio (Chord AI) entitlements for a tier. Mirrors the backend
 * matrix in SubscriptionsController.GetTierFeatures so the UI can warn about
 * data loss before a downgrade revokes access.
 *   - chordAi: AI chord detection (Song Studio)
 *   - audioSeparation: AI stem separation
 * Note: founders is a basic-priced tier that unlocks pro features, so it must
 * be handled explicitly (normalizeTier does not know about it).
 */
export const getTierFeatures = (tier: string): { chordAi: boolean; audioSeparation: boolean } => {
  switch (tier.toLowerCase()) {
    case 'basic':
      return { chordAi: true, audioSeparation: false };
    case 'founders':
    case 'pro':
    case 'enterprise':
      return { chordAi: true, audioSeparation: true };
    case 'free':
    default:
      return { chordAi: false, audioSeparation: false };
  }
};

// Paid workflow gates the admin UI teases on Free. Mirrors the backend
// entitlement matrix in SubscriptionsController.IsFeatureEnabled; only the
// features the UI actually gates are surfaced here.
export interface SubscriptionFeatures {
  batchImport: boolean;
  serviceTemplates: boolean;
}

export const getSubscriptionFeatures = (tier: string): SubscriptionFeatures => {
  const isPaid = normalizeTier(tier) !== 'free';
  return {
    batchImport: isPaid,
    serviceTemplates: isPaid,
  };
};
